import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader, Eye, EyeOff, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import AutocompleteSelect from '../components/AutocompleteSelect';
import OpeningHoursEditor, { serializeOpeningHours } from '../components/OpeningHoursEditor';
import PinLocationPicker from '../components/PinLocationPicker';
import SignatureServices from '../components/SignatureServices';
import TermsConsent from '../components/TermsConsent';
import { getMe, createDoctor, verifyKMPDC, getSpecialties, getCounties, getInsurances, getDoctorTitles } from '../services/api';
import { toast } from 'sonner';
import api from '../services/api';

export default function DoctorRegistration() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [kmpdcVerified, setKmpdcVerified] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [specialties, setSpecialties] = useState([]);
  const [counties, setCounties] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [titles, setTitles] = useState([]);
  
  // Account credentials for new users
  const [accountData, setAccountData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    specialty: '',
    region: '',
    kmpdc_license: '',
    bio: '',
    education: '',
    credentials: '',
    address: '',
    contact_phone: '',
    contact_whatsapp: '',
    contact_email: '',
    website: '',
    key_interests: '',
    affiliations: [],
    insurances_accepted: [],
    photo_url: '',
    opening_hours: {
      weekdays: '08:00 - 17:00',
      saturday: '09:00 - 13:00',
      sunday: 'Closed',
      public_holidays: 'Closed'
    },
    pin_location: null,
    signature_services: [],
    terms_accepted: false
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
        
        // Check for pending registration data
        const pendingData = localStorage.getItem('pending_doctor_registration');
        if (pendingData) {
          const parsed = JSON.parse(pendingData);
          setFormData(prev => ({ ...parsed, name: response.data.name, contact_email: response.data.email }));
        } else {
          setFormData(prev => ({ ...prev, name: response.data.name, contact_email: response.data.email }));
        }
      } catch (error) {
        // User not logged in - that's okay, they can register and will be prompted to login
        setUser(null);
        
        // Still load pending data if available
        const pendingData = localStorage.getItem('pending_doctor_registration');
        if (pendingData) {
          setFormData(JSON.parse(pendingData));
        }
      }
    };
    checkAuth();
    
    // Load dropdown options
    const loadOptions = async () => {
      try {
        console.log('Loading dropdown options...');
        const [specialtiesRes, countiesRes, insurancesRes, titlesRes] = await Promise.all([
          getSpecialties(),
          getCounties(),
          getInsurances(),
          getDoctorTitles()
        ]);
        console.log('Loaded options:', {
          specialties: specialtiesRes.data?.length,
          counties: countiesRes.data?.length,
          insurances: insurancesRes.data?.length,
          titles: titlesRes.data?.length
        });
        setSpecialties(specialtiesRes.data || []);
        setCounties(countiesRes.data || []);
        setInsurances(insurancesRes.data || []);
        setTitles(titlesRes.data || []);
      } catch (error) {
        console.error('Error loading options:', error);
        // Set fallback options if API fails
        setTitles(['Dr', 'Prof', 'Mr', 'Mrs', 'Ms']);
        setSpecialties(['General Medicine', 'Cardiology', 'Pediatrics', 'Dermatology', 'Gynecology', 'Dentistry', 'Orthopedics', 'Psychiatry', 'Neurology', 'Oncology']);
        setCounties(['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Kiambu', 'Machakos', 'Nyeri', 'Meru', 'Kakamega']);
        setInsurances(['NHIF', 'AAR', 'Jubilee Insurance', 'Britam', 'CIC Insurance', 'Madison Insurance', 'UAP Insurance', 'Heritage Insurance']);
      }
    };
    loadOptions();
  }, [navigate]);

  const handleVerifyKMPDC = async () => {
    if (!formData.kmpdc_license || !formData.name) {
      toast.error('Please enter KMPDC license number and name');
      return;
    }

    setVerifying(true);
    try {
      const response = await verifyKMPDC(formData.kmpdc_license, formData.name);
      setKmpdcVerified(response.data);
      
      if (response.data.verified) {
        toast.success('KMPDC license verified successfully!');
      } else {
        toast.warning('KMPDC verification pending. ' + response.data.message);
      }
    } catch (error) {
      console.error('KMPDC verification error:', error);
      toast.error('Failed to verify KMPDC license');
      setKmpdcVerified({ verified: false, error: 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.name || !formData.specialty || !formData.region) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // If user is not logged in, create account first
    if (!user) {
      if (!accountData.email || !accountData.password) {
        toast.error('Please enter your email and password to create an account');
        return;
      }
      
      if (accountData.password !== accountData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      
      if (accountData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);
    try {
      let currentUser = user;
      
      // If not logged in, create account first
      if (!user) {
        try {
          const signupResponse = await api.post('/auth/email/signup', {
            name: formData.name,
            email: accountData.email,
            password: accountData.password
          });
          
          // Store session token
          localStorage.setItem('session_token', signupResponse.data.session_token);
          localStorage.setItem('user', JSON.stringify(signupResponse.data.user));
          currentUser = signupResponse.data.user;
          setUser(currentUser);
          
          toast.success('Account created successfully!');
        } catch (signupError) {
          console.error('Signup error:', signupError);
          const errorMsg = signupError.response?.data?.detail || 'Failed to create account';
          toast.error(errorMsg);
          setLoading(false);
          return;
        }
      }
      
      // Now create doctor profile
      const doctorData = {
        ...formData,
        contact_email: accountData.email || formData.contact_email,
        affiliations: formData.affiliations.filter(a => a.trim()),
        insurances_accepted: formData.insurances_accepted
      };
      
      await createDoctor(doctorData);
      
      // Clear any pending registration data
      localStorage.removeItem('pending_doctor_registration');
      
      toast.success('Doctor profile created successfully!');
      navigate('/doctor/dashboard');
    } catch (error) {
      console.error('Doctor registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create doctor profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInsuranceToggle = (insurance) => {
    const current = formData.insurances_accepted;
    if (current.includes(insurance)) {
      setFormData({ ...formData, insurances_accepted: current.filter(i => i !== insurance) });
    } else {
      setFormData({ ...formData, insurances_accepted: [...current, insurance] });
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload/photo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ ...formData, photo_url: response.data.photo_url });
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData({ ...formData, photo_url: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-lg flex items-center justify-center">
              <Shield className="h-6 w-6 text-[#2DD4BF]" />
            </div>
            <div>
              <h1 className="font-manrope font-bold text-3xl text-[#0F2A44]">
                Doctor Registration
              </h1>
              <p className="font-public text-sm text-[#4B5563]">
                Create your professional profile on Afyagram
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Creation - Only show if user is not logged in */}
            {!user && (
              <div className="space-y-4 bg-[#0F2A44]/5 rounded-lg p-6 border border-[#0F2A44]/10">
                <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">
                  Create Your Account
                </h2>
                <p className="font-public text-sm text-[#4B5563]">
                  Your account credentials for accessing your profile
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Email Address *</label>
                  <Input
                    type="email"
                    required
                    value={accountData.email}
                    onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                    placeholder="doctor@example.com"
                    data-testid="account-email-input"
                    className="h-12"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#262626] mb-2">Password *</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        required
                        value={accountData.password}
                        onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                        placeholder="Min 6 characters"
                        data-testid="account-password-input"
                        className="h-12 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#262626] mb-2">Confirm Password *</label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      required
                      value={accountData.confirmPassword}
                      onChange={(e) => setAccountData({ ...accountData, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      data-testid="account-confirm-password-input"
                      className="h-12"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {user && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="font-public text-sm text-green-800">
                  ✓ Signed in as <strong>{user.email}</strong>
                </p>
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Basic Information</h2>
              
              {/* Profile Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Profile Photo</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="doctor-photo-input"
                />
                
                {(photoPreview || formData.photo_url) ? (
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <img 
                        src={photoPreview || (formData.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.photo_url}` : formData.photo_url)}
                        alt="Profile preview" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
                      />
                      {uploadingPhoto && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                          <Loader className="h-6 w-6 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                      >
                        Change Photo
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removePhoto}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#2DD4BF] hover:bg-[#2DD4BF]/5 transition-colors"
                  >
                    <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                    <p className="font-public text-sm text-[#4B5563] mb-1">
                      Click to upload your photo
                    </p>
                    <p className="text-xs text-slate-400">
                      JPEG, PNG or WebP (max 5MB)
                    </p>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Title *</label>
                  <AutocompleteSelect
                    options={titles}
                    value={formData.title}
                    onChange={(value) => setFormData({ ...formData, title: value })}
                    placeholder="Select title"
                    dataTestId="doctor-title-select"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Full Name *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="doctor-name-input"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Specialty *</label>
                  <AutocompleteSelect
                    options={specialties}
                    value={formData.specialty}
                    onChange={(value) => setFormData({ ...formData, specialty: value })}
                    placeholder="Select specialty"
                    dataTestId="doctor-specialty-select"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">County *</label>
                  <AutocompleteSelect
                    options={counties}
                    value={formData.region}
                    onChange={(value) => setFormData({ ...formData, region: value })}
                    placeholder="Select county"
                    dataTestId="doctor-region-select"
                  />
                </div>
              </div>

              {/* KMPDC License Verification */}
              <div className="bg-[#4A90E2]/5 rounded-lg p-4 border border-[#4A90E2]/20">
                <label className="block text-sm font-medium text-[#262626] mb-2">
                  KMPDC License Number
                </label>
                <div className="flex gap-3">
                  <Input
                    value={formData.kmpdc_license}
                    onChange={(e) => setFormData({ ...formData, kmpdc_license: e.target.value })}
                    placeholder="Enter KMPDC license number"
                    data-testid="kmpdc-license-input"
                    className="h-12"
                  />
                  <Button
                    type="button"
                    onClick={handleVerifyKMPDC}
                    disabled={verifying || !formData.kmpdc_license}
                    data-testid="verify-kmpdc-btn"
                    className="bg-[#4A90E2] hover:bg-[#4A90E2]/90 text-white"
                  >
                    {verifying ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify'
                    )}
                  </Button>
                </div>
                
                {kmpdcVerified && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    kmpdcVerified.verified ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'
                  }`}>
                    <p className="text-sm font-public">{kmpdcVerified.message}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Professional Details */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Professional Details</h2>
              
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Bio</label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief professional bio"
                  rows={4}
                  data-testid="doctor-bio-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Education</label>
                <Input
                  value={formData.education}
                  onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                  placeholder="Medical school and qualifications"
                  data-testid="doctor-education-input"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Credentials & Certifications</label>
                <Input
                  value={formData.credentials}
                  onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
                  placeholder="Board certifications, fellowships"
                  data-testid="doctor-credentials-input"
                  className="h-12"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Contact Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Phone Number</label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+254-XXX-XXXXXX"
                    data-testid="doctor-phone-input"
                    className="h-12"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">WhatsApp Number</label>
                  <Input
                    value={formData.contact_whatsapp}
                    onChange={(e) => setFormData({ ...formData, contact_whatsapp: e.target.value })}
                    placeholder="+254XXXXXXXXX"
                    data-testid="doctor-whatsapp-input"
                    className="h-12"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Email</label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  data-testid="doctor-email-input"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Clinic/Practice Address</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                  data-testid="doctor-address-input"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Website</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://yourwebsite.com"
                  data-testid="doctor-website-input"
                  className="h-12"
                />
              </div>
            </div>

            {/* Opening Hours */}
            <div className="space-y-4">
              <OpeningHoursEditor
                value={formData.opening_hours}
                onChange={(hours) => setFormData({ ...formData, opening_hours: hours })}
              />
            </div>

            {/* Pin Location */}
            <PinLocationPicker
              value={formData.pin_location}
              onChange={(loc) => setFormData({ ...formData, pin_location: loc })}
            />

            {/* Insurance Companies */}
            <div className="space-y-4">
              <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">Insurances Accepted</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-4 border border-slate-200 rounded-lg">
                {insurances.map((insurance) => (
                  <label key={insurance} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.insurances_accepted.includes(insurance)}
                      onChange={() => handleInsuranceToggle(insurance)}
                      className="h-4 w-4 text-[#2DD4BF] rounded focus:ring-[#2DD4BF]"
                    />
                    <span className="font-public text-sm text-[#262626]">{insurance}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Signature Services */}
            <SignatureServices
              value={formData.signature_services}
              onChange={(svcs) => setFormData({ ...formData, signature_services: svcs })}
            />

            {/* Terms & Conditions */}
            <TermsConsent
              accepted={formData.terms_accepted}
              onChange={(val) => setFormData({ ...formData, terms_accepted: val })}
            />

            {/* Submit Button */}
            <div className="flex gap-4 pt-6">
              <Button
                type="button"
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !formData.terms_accepted}
                data-testid="doctor-register-submit"
                className="flex-1 bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-manrope font-semibold disabled:opacity-50"
              >
                {loading ? 'Creating Profile...' : 'Create Doctor Profile'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
